import type { Component } from "svelte"
import { get, writable } from "svelte/store"
import { v4 as uuid } from "uuid"

export type OverlayOptions<T extends {}> = {
	heading?: string
	classOverlay?: string
	classHeading?: string
	hideExitButton?: boolean
	zIndex?: number
	props?: T
	beforeClose?: () => Promise<void>
}

export namespace OverlayController {
	let currentIndex = 0

	// `options` is REQUIRED here even though it is optional on `openOverlay`.
	// openOverlay normalises `options ?? undefined` into a concrete `opts` object
	// and always writes it (see the store.update below), and openOverlay is the
	// only writer of this store — so nothing in the store has ever had an absent
	// `options`. Declaring it optional only forced every reader to re-check
	// something that cannot happen. Type-level only: no value changes.
	type ActiveOverlay = {
		component: Component<any>
		options: OverlayOptions<any>
	}

	export type ContextType = {
		[index: string]: ActiveOverlay
	}

	const store = writable<ContextType>({})

	/** Subscribe to the overlay store */
	export function subscribe(cb: (ctx: ContextType) => void) {
		return store.subscribe(cb)
	}

	/**
	 * Mounts a new overlay
	 * @param component the component to mount as an overlay
	 * @param options the component options (props, ect)
	 */
	export function openOverlay<T extends {}>(component: Component<T>, options?: OverlayOptions<T>) {
		const opts: OverlayOptions<T> = { ...options }
		if (!opts.zIndex) opts.zIndex = ++currentIndex

		const id = uuid()

		store.update((ctx) => ({
			...ctx,
			[id]: {
				component,
				options: opts,
			},
		}))

		return id
	}

	/**
	 * Closes the provided overlay (or all overlays if uuid is not provided)
	 */
	export async function closeOverlay(uuid?: string, force?: boolean) {
		if (uuid) {
			const overlayContext = get(store)[uuid]
			if (overlayContext?.options?.beforeClose && !force)
				await overlayContext?.options?.beforeClose()

			store.update((ctx) => {
				const currentState = ctx
				delete currentState[uuid]
				return currentState
			})
		} else {
			const activeOverlays = Object.values(get(store))
			for (const overlay of activeOverlays) {
				if (overlay.options?.beforeClose && !force) await overlay.options.beforeClose()
			}

			store.set({})
		}

		updateZIndex()
	}

	function updateZIndex() {
		const active = get(store)
		let topZIndex = -9999
		for (const [id, overlay] of Object.entries(active)) {
			// `zIndex` stays optional on the public OverlayOptions (callers may omit
			// it and let openOverlay assign one), so it has to be narrowed here.
			// This is the SAME branch as before, not a new one: the old comparison
			// `undefined > topZIndex` is a NaN comparison, which is always false, so
			// an overlay without a zIndex was already skipped. Spelling the check out
			// just lets the compiler see what the comparison was already doing.
			const zIndex = overlay.options.zIndex
			if (zIndex !== undefined && zIndex > topZIndex) topZIndex = zIndex
		}

		currentIndex = topZIndex !== -9999 ? topZIndex : 0
	}
}
